// src/components/seller/CatalogAuth.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../utils/helpers';
import { 
  ShoppingBag, 
  Facebook, 
  Instagram, 
  Zap, 
  CheckCircle, 
  AlertCircle, 
  ArrowLeft,
  Link2,
  RefreshCw,
  Download,
  Upload,
  ExternalLink
} from 'lucide-react';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const CatalogAuth = ({ onBack }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [catalogStatus, setCatalogStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('whatsapp');
  const [syncProgress, setSyncProgress] = useState(null);

  // Fetch catalog status on component mount
  useEffect(() => {
    fetchCatalogStatus();
  }, []);

  const fetchCatalogStatus = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`${API_CONFIG.META_CATALOG.STATUS}/${user?.company_id}`);
      
      if (response.data.success) {
        setCatalogStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching catalog status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchImportedProducts = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`${API_CONFIG.META_CATALOG.PRODUCTS}/${user?.company_id}`);
      
      if (response.data.success) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      showToast('Failed to fetch products', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectCatalog = async () => {
    try {
      setIsLoading(true);
      // Open authorization window
      const authWindow = window.open(
        `${process.env.REACT_APP_BACKEND_URL}/api/meta-catalog/auth/initiate?sellerId=${user?.company_id}&redirectAfterAuth=/seller/catalog/dashboard`,
        '_blank',
        'width=600,height=700,menubar=no,toolbar=no,location=yes'
      );
      
      // Poll for connection status
      const checkInterval = setInterval(async () => {
        const statusRes = await api.get(`${API_CONFIG.META_CATALOG.STATUS}/${user?.company_id}`);
        if (statusRes.data.success && statusRes.data.data.connected) {
          clearInterval(checkInterval);
          setCatalogStatus(statusRes.data.data);
          showToast('Catalog connected successfully!', 'success');
          if (authWindow) authWindow.close();
        }
      }, 3000);
      
      // Stop polling after 5 minutes
      setTimeout(() => clearInterval(checkInterval), 300000);
      
    } catch (error) {
      console.error('Error connecting catalog:', error);
      showToast('Failed to connect catalog', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncProducts = async () => {
    try {
      setIsSyncing(true);
      setSyncProgress({ stage: 'fetching', message: 'Fetching products from WhatsApp catalog...' });
      
      const response = await api.post(`${API_CONFIG.META_CATALOG.SYNC}/${user?.company_id}`);
      
      if (response.data.success) {
        setSyncProgress({ stage: 'complete', message: `Synced ${response.data.count} products successfully!` });
        showToast(response.data.message, 'success');
        await fetchImportedProducts();
        
        // Clear progress after 3 seconds
        setTimeout(() => setSyncProgress(null), 3000);
      } else {
        setSyncProgress({ stage: 'error', message: response.data.error });
        showToast(response.data.error, 'error');
        setTimeout(() => setSyncProgress(null), 3000);
      }
    } catch (error) {
      console.error('Error syncing products:', error);
      setSyncProgress({ stage: 'error', message: 'Failed to sync products' });
      showToast('Failed to sync products', 'error');
      setTimeout(() => setSyncProgress(null), 3000);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your catalog? Your imported products will remain but new syncs will stop.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await api.delete(`/meta-catalog/disconnect/${user?.company_id}`);
      
      if (response.data.success) {
        setCatalogStatus(null);
        showToast('Catalog disconnected successfully', 'success');
      }
    } catch (error) {
      console.error('Error disconnecting catalog:', error);
      showToast('Failed to disconnect catalog', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadToWhatsApp = async (product) => {
    try {
      setIsLoading(true);
      const response = await api.post(`${API_CONFIG.META_CATALOG.UPLOAD}/${user?.company_id}`, product);
      
      if (response.data.success) {
        showToast('Product uploaded to WhatsApp catalog successfully!', 'success');
      }
    } catch (error) {
      console.error('Error uploading product:', error);
      showToast('Failed to upload product', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full mt-3 animate-fade-in">
      {/* Back Button */}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors mb-6 group"
      >
        <div className="p-1 rounded-full border border-gray-200 group-hover:border-blue-200 group-hover:bg-blue-50 transition-all">
          <ArrowLeft size={18} className="group-hover:text-blue-600" />
        </div>
        <span className="text-sm font-medium">Back to dashboard</span>
      </button>

      <div className="mb-8">
        <h2 className="text-2xl font-black text-gray-900 mb-2">Product Catalog Integration</h2>
        <p className="text-gray-500">
          Connect your WhatsApp and Instagram catalogs to sync products seamlessly
        </p>
      </div>

      {/* Platform Selection Tabs */}
      <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-2xl">
        <button
          onClick={() => setSelectedPlatform('whatsapp')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
            selectedPlatform === 'whatsapp'
              ? 'bg-white text-green-600 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.032 0C5.384 0 .022 5.38 0 12.038c0 2.128.557 4.202 1.608 6.016L.015 24l6.007-1.573c1.76.96 3.746 1.466 5.784 1.467h.005c6.647 0 12.009-5.382 12.009-12.04C23.794 5.38 18.432 0 12.032 0zm0 22.08c-1.824 0-3.612-.492-5.18-1.425l-.37-.22-3.565.935.95-3.48-.24-.383A9.016 9.016 0 0 1 12.032 2.99c4.993 0 9.053 4.063 9.053 9.058 0 4.993-4.06 9.053-9.053 9.053z"/>
            <path d="M16.61 13.98c-.15-.075-.887-.437-1.024-.487-.138-.05-.238-.075-.338.075-.1.15-.388.487-.475.587-.088.1-.175.112-.325.038-.15-.075-.633-.233-1.206-.744-.447-.398-.748-.89-.836-1.04-.088-.15-.01-.232.066-.307.067-.067.15-.175.225-.262.075-.088.1-.15.15-.25.05-.1.025-.188-.013-.263-.037-.075-.338-.814-.463-1.115-.122-.293-.246-.253-.338-.258-.087-.005-.187-.005-.287-.005-.1 0-.262.037-.4.188-.137.15-.525.513-.525 1.25 0 .738.537 1.45.612 1.55.075.1 1.056 1.613 2.558 2.262.357.154.636.246.854.314.358.11.684.095.942.058.287-.038.887-.363 1.012-.713.125-.35.125-.65.088-.712-.038-.063-.137-.1-.288-.176z"/>
          </svg>
          WhatsApp Catalog
        </button>
        <button
          onClick={() => setSelectedPlatform('instagram')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
            selectedPlatform === 'instagram'
              ? 'bg-white text-pink-600 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          disabled
        >
          <Instagram size={20} />
          Instagram (Coming Soon)
        </button>
      </div>

      {/* Connection Status Card */}
      <div className="bg-gray-50 rounded-2xl p-6 mb-6 border border-gray-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
              catalogStatus?.connected ? 'bg-green-100' : 'bg-gray-200'
            }`}>
              {catalogStatus?.connected ? (
                <CheckCircle size={28} className="text-green-600" />
              ) : (
                <ShoppingBag size={28} className="text-gray-500" />
              )}
            </div>
            <div>
              <h3 className="font-bold text-gray-900">
                {catalogStatus?.connected ? 'Catalog Connected' : 'No Catalog Connected'}
              </h3>
              <p className="text-sm text-gray-500">
                {catalogStatus?.connected 
                  ? `Connected to ${catalogStatus.catalogName || 'your WhatsApp catalog'}`
                  : 'Connect your catalog to sync products automatically'}
              </p>
            </div>
          </div>

          {!catalogStatus?.connected ? (
            <button
              onClick={handleConnectCatalog}
              disabled={isLoading}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-all disabled:opacity-50"
            >
              <Link2 size={18} />
              {isLoading ? 'Connecting...' : 'Connect Catalog'}
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={handleSyncProducts}
                disabled={isSyncing}
                className="flex items-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-500 text-white rounded-xl font-medium transition-all disabled:opacity-50"
              >
                <RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} />
                {isSyncing ? 'Syncing...' : 'Sync Products'}
              </button>
              <button
                onClick={handleDisconnect}
                disabled={isLoading}
                className="flex items-center gap-2 px-5 py-3 border border-red-200 text-red-600 hover:bg-red-50 rounded-xl font-medium transition-all disabled:opacity-50"
              >
                Disconnect
              </button>
            </div>
          )}
        </div>

        {/* Sync Progress */}
        {syncProgress && (
          <div className={`mt-4 p-3 rounded-xl ${
            syncProgress.stage === 'error' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
          }`}>
            <div className="flex items-center gap-2">
              {syncProgress.stage === 'fetching' && <RefreshCw size={16} className="animate-spin" />}
              {syncProgress.stage === 'complete' && <CheckCircle size={16} />}
              {syncProgress.stage === 'error' && <AlertCircle size={16} />}
              <span className="text-sm">{syncProgress.message}</span>
            </div>
          </div>
        )}

        {/* Connection Info */}
        {catalogStatus?.connected && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Catalog ID:</span>
                <p className="font-mono text-xs text-gray-700 mt-1">{catalogStatus.catalogId}</p>
              </div>
              <div>
                <span className="text-gray-500">Last Sync:</span>
                <p className="font-medium text-gray-700 mt-1">
                  {catalogStatus.lastSyncAt ? new Date(catalogStatus.lastSyncAt).toLocaleString() : 'Never'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Products Synced:</span>
                <p className="font-bold text-gray-900 mt-1">{catalogStatus.productCount || 0}</p>
              </div>
              <div>
                <span className="text-gray-500">Token Expires:</span>
                <p className="text-sm text-gray-700 mt-1">
                  {catalogStatus.tokenExpiresAt ? new Date(catalogStatus.tokenExpiresAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Features Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Download size={20} className="text-blue-600" />
            </div>
            <h4 className="font-bold text-gray-900">Import from WhatsApp</h4>
          </div>
          <p className="text-sm text-gray-500">
            Automatically import all products from your WhatsApp catalog to your Paymeo seller dashboard
          </p>
        </div>
        
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <Upload size={20} className="text-green-600" />
            </div>
            <h4 className="font-bold text-gray-900">Export to WhatsApp</h4>
          </div>
          <p className="text-sm text-gray-500">
            Publish products from your Paymeo store directly to your WhatsApp catalog
          </p>
        </div>
      </div>

      {/* Synced Products Section */}
      {catalogStatus?.connected && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-gray-900">Synced Products</h3>
            <button
              onClick={fetchImportedProducts}
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>

          {products.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl">
              <ShoppingBag size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No products synced yet</p>
              <button
                onClick={handleSyncProducts}
                className="mt-3 text-blue-600 hover:underline text-sm"
              >
                Sync products from your catalog →
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.slice(0, 6).map((product) => (
                <div key={product.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                  {product.image_url && (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-40 object-cover rounded-lg mb-3"
                    />
                  )}
                  <h4 className="font-bold text-gray-900 truncate">{product.name}</h4>
                  <p className="text-green-600 font-bold mt-1">₦{parseFloat(product.price).toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-2 truncate">{product.description}</p>
                  <button
                    onClick={() => handleUploadToWhatsApp(product)}
                    className="mt-3 w-full py-2 text-center text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Upload size={14} className="inline mr-1" />
                    Upload to WhatsApp
                  </button>
                </div>
              ))}
            </div>
          )}

          {products.length > 6 && (
            <div className="text-center mt-4">
              <button className="text-blue-600 hover:underline text-sm">
                View all {products.length} products →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="mt-8 p-4 bg-blue-50 rounded-xl">
        <div className="flex items-start gap-3">
          <Zap size={20} className="text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">Need help connecting your catalog?</p>
            <p className="text-sm text-blue-700 mt-1">
              Make sure you have:
              <br />• A WhatsApp Business account with an approved catalog
              <br />• Admin access to your business's Facebook page
              <br />• Your catalog set up in Commerce Manager
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogAuth;